import React from "react";
//#region types
import { FormEnums } from "models/enums";
import { AccountingFormTypes } from "models/formTypes";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#region components
import { useFinancialReportService } from "serviceHooks";
//#endregion
import { DateHelper, QueryStateHelper } from "utils/helpers";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";

function useReconciliationReportFilterDefinition() {
  const financialReportService = useFinancialReportService();
  const [filterValues, setFilterItems] = React.useState<WintSuperkollAPIModelFinancialReport.AuditReportFilterItem[]>(
    []
  );

  const [currentReconciliationId, setCurrentReconciliationId] = React.useState<number | undefined>();
  const [currentReconciliation, setCurrentReconciliation] = React.useState<string | undefined>();

  React.useEffect(() => {
    financialReportService.getAuditBalanceReportFilterData().then((res) => setFilterItems(res.filterItems ?? []));
  }, []);

  const getDisplayValue = (item: WintSuperkollAPIModelFinancialReport.AuditReportFilterItem): string =>
    DateHelper.formatAPIYearMonthRange(item.startMonth, item.endMonth);

  React.useEffect(() => {
    const initialFilterValue = filterValues?.length > 0 ? filterValues[0] : undefined;

    setCurrentReconciliationId(initialFilterValue ? initialFilterValue.reconciliationId : undefined);
    setCurrentReconciliation(initialFilterValue ? getDisplayValue(initialFilterValue) : undefined);
  }, [filterValues]);

  const reconciliations = React.useMemo(() => {
    return filterValues.map(
      (item, index): AutocompleteListItem => ({
        id: item.reconciliationId,
        value: getDisplayValue(item),
        data: item,
      })
    );
  }, [filterValues]);

  const filterFormFields: GenericFormSection<AccountingFormTypes.ReconciliationReportFilter>[] = React.useMemo(
    () => [
      {
        label: TranslationKeys.Accounting.Reconciliation,
        type: FormEnums.FormSectionType.EXPANDABLESECTION,
        icon: IconDefinitionConstants.WMonthCalendar,
        innerFields: {
          reconciliation: {
            label: TranslationKeys.Common.RefersTo,
            type: FormEnums.FormFieldType.AUTOCOMPLETE,
            extraProps: {
              referenceFieldName: FormFieldConstants.Accounting.ReconciliationReportFilter.ReconciliationId,
              items: reconciliations,
              disableClear: true,
            } as AutoCompleteExtraProps<AccountingFormTypes.ReconciliationReportFilter>,
          },
        },
      },
    ],
    [reconciliations]
  );

  const apiToReportReconciliationFilterFormatter = React.useCallback(
    async (filter: WintSuperkollAPIModelFinancialReport.AuditReportFilter) => {
      const reportFilter: AccountingFormTypes.ReconciliationReportFilter = {
        reconciliationId: currentReconciliationId,
      };

      filter[FormFieldConstants.Accounting.ReconciliationReportFilter.ReconciliationId] = currentReconciliationId;

      if (filter.reconciliationId) {
        const reconciliation = filterValues?.find((p) => p.reconciliationId === filter.reconciliationId);
        if (reconciliation) reportFilter.reconciliation = getDisplayValue(reconciliation);
      }

      return reportFilter;
    },
    [currentReconciliationId]
  );

  const filterQueryStateHandler: FilterQueryStateHandler<AccountingFormTypes.ReconciliationReportFilter> = {
    reconciliationId: QueryStateHelper.numberQueryEncoder(
      FormFieldConstants.Accounting.ReconciliationReportFilter.ReconciliationId
    ),
    reconciliation: QueryStateHelper.stringQueryEncoder(
      FormFieldConstants.Accounting.ReconciliationReportFilter.Reconciliation
    ),
  };

  return {
    filterFormFields,
    apiToReportReconciliationFilterFormatter,
    currentReconciliationId,
    currentReconciliation,
    filterValues,
    filterQueryStateHandler,
  };
}

export default useReconciliationReportFilterDefinition;
